/* eslint-disable no-useless-concat */

/* eslint-disable react-hooks/exhaustive-deps */
import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import { formatDate } from "../../shared/services/utils";
import { MapInfo, VesselInfo, VoyageVesselInfo } from "../../tech-form/models/FormInterfaces";
import ChecklistTab from "../components/ChecklistTab";
import VesselInformation from "../components/VesselInformation";
import VoyageInformation from "../components/VoyageInformation";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const VerticalDivider = () => {
  const theme = useAtomValue(themeAtom);

  return <div className={twMerge("w-px self-stretch", theme.vesselDetails.divider)} />;
};

const VoyagePage: React.FC = () => {
  const theme = useAtomValue(themeAtom);

  const { tcCode } = useParams();
  const [vesselInfo, setVesselInfo] = useState<VesselInfo | null>(null);
  const [voyageVesselInfo, setVoyageVesselInfo] = useState<VoyageVesselInfo | null>(null);
  const [mapData, setMapData] = useState<MapInfo | null>(null);
  const [voyageInfoData, setVoyageInfoData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMapData = async (imo: number) => {
    try {
      setIsLoading(true);
      const voyagePositionResult = await api.get(`${apiUrl}/voyagePosition`, {
        params: {
          imo,
        },
      });
      const item = voyagePositionResult.data;
      setMapData((prevMapData) => ({
        "Current position": formatCoordinates(item?.LATITUDE || 0, item?.LONGITUDE || 0),
        "Current speed": item.CURRENT_SPEED || item.CURRENT_SPEED === 0 ? `${item.CURRENT_SPEED} knots` : "",
        "Current Draft": item.DRAFT || item.DRAFT === 0 ? `${item.DRAFT} m` : "",
        "ETA Next Port": prevMapData?.["ETA Next Port"] ?? "N/A",
        "Next open date": prevMapData?.["Next open date"] ?? "N/A",
        "Next open port": prevMapData?.["Next open port"] ?? "N/A",
      }));
      setIsLoading(false);
    } catch (error: unknown) {
      setIsLoading(false);
    }
  };

  const fetchVoyageMovementsPort = async () => {
    try {
      setIsLoading(true);
      const voyageMovementsResult = await api.get(`${apiUrl}/voyageMovementsPort`, {
        params: {
          tc_code: tcCode,
        },
      });

      const vi = voyageMovementsResult.data;

      const vesselInfo: VesselInfo = {
        VESSEL_CODE: vi.VESSEL_CODE ?? 0,
        VESSEL_NAME: vi.VESSEL_NAME || "",
        VOYAGE_NO: vi.VOYAGE_NO ?? 0,
        VOYAGE_STATUS_CODE: vi.VOYAGE_STATUS_CODE || "Pending",
        COMMENCE_DATE_GMT: vi.COMMENCE_DATE_GMT ? new Date(vi.COMMENCE_DATE_GMT) : new Date(),
        COMPLETED_DATE_GMT: vi.COMPLETED_DATE_GMT ? new Date(vi.COMPLETED_DATE_GMT) : null,
        OPR_TYPE: vi.OPR_TYPE || "",
        IMO_NUMBER: vi.IMO_NUMBER ?? 0,
        YEAR_BUILT: vi.YEAR_BUILT ?? 0,
        OWNER: vi.OWNER ?? 0,
        VESSEL_TYPE: vi.VESSEL_TYPE || "",
        CLASS_SOCIETY_CODE: vi.CLASS_SOCIETY_CODE || null,
        EMAIL: vi.EMAIL || "",
        DWT: vi.DWT ?? 0,
        VESSEL_SUMMER_DRAFT: vi.VESSEL_SUMMER_DRAFT ?? 0,
        TPC: vi.TPC ?? 0,
        CAPACITY_BALE_M3: vi.CAPACITY_BALE_M3 ?? 0,
        CAPACITY_BALE_FT3_REFERENCE: vi.CAPACITY_BALE_FT3_REFERENCE ?? 0,
        CAPACITY_BALE_FT3: vi.CAPACITY_BALE_FT3 ?? 0,
        CAPACITY_GRAIN_M3: vi.CAPACITY_GRAIN_M3 ?? 0,
        CAPACITY_GRAIN_FT3_REFERENCE: vi.CAPACITY_GRAIN_FT3_REFERENCE ?? 0,
        CAPACITY_GRAIN_FT3: vi.CAPACITY_GRAIN_FT3 ?? 0,
        SCRUBBER_TYPE: vi.SCRUBBER_TYPE || null,
        LATITUDE: vi.LATITUDE ?? 0,
        LONGITUDE: vi.LONGITUDE ?? 0,
        CURRENT_SPEED: vi.CURRENT_SPEED ?? 0,
        DRAFT: vi.DRAFT ?? 0,
        ETA_NEXT_PORT: vi.ETA_NEXT_PORT ? new Date(vi.ETA_NEXT_PORT) : null,
        NEXT_OPEN_DATE: vi.NEXT_OPEN_DATE ? new Date(vi.NEXT_OPEN_DATE) : null,
        NEXT_OPEN_PORT_NAME: vi.NEXT_OPEN_PORT_NAME || null,
        TC_CODE: vi.TC_CODE || "",
        TIMECHARTERCONTRACT_STATUS: vi.TIMECHARTERCONTRACT_STATUS || "Delivered",
      };

      setVesselInfo(vesselInfo);
      setMapData((prevMapData) => ({
        "Current position": prevMapData?.["Current position"] ?? "N/A",
        "Current speed": prevMapData?.["Current speed"] ?? "N/A",
        "Current Draft": prevMapData?.["Current Draft"] ?? "N/A",
        "ETA Next Port": vi.ETA_NEXT_PORT ? formatDate(vi.ETA_NEXT_PORT) : "N/A",
        "Next open date": vi.NEXT_OPEN_DATE ? formatDate(vi.NEXT_OPEN_DATE) : "N/A",
        "Next open port": vi.NEXT_OPEN_PORT_NAME || "N/A",
      }));
      setIsLoading(false);
    } catch (error: unknown) {
      logE(new Error("VoyagePage - fetchVoyageMovementsPort - failed"));
      setIsLoading(false);
    }
  };

  const fetchVoyageVesselInfo = async () => {
    try {
      setIsLoading(true);
      const voyageMovementsResult = await api.get<VoyageVesselInfo>(`${apiUrl}/vessel-info`, {
        params: { tcCode },
      });

      const vi = voyageMovementsResult.data;

      const vesselInfo: VoyageVesselInfo = {
        VESSEL_CODE: vi.VESSEL_CODE ?? 0,
        VESSEL_NAME: vi.VESSEL_NAME || "",
        IMO_NUMBER: vi.IMO_NUMBER ?? 0,
        YEAR_BUILT: vi.YEAR_BUILT ?? 0,
        OWNER: vi.OWNER ?? 0,
        VESSEL_TYPE: vi.VESSEL_TYPE || "",
        CLASS_SOCIET_YCODE: vi.CLASS_SOCIET_YCODE,
        EMAIL: vi.EMAIL || "",
        DWT: vi.DWT ?? 0,
        VESSEL_SUMMER_DRAFT: vi.VESSEL_SUMMER_DRAFT ?? 0,
        TPC: vi.TPC ?? 0,
        CAPACITY_BALE_M3: vi.CAPACITY_BALE_M3 ?? 0,
        CAPACITY_GRAIN_M3: vi.CAPACITY_GRAIN_M3 ?? 0,
        CAPACITY_BALE_FT3: vi.CAPACITY_BALE_FT3 ?? 0,
        CAPACITY_GRAIN_FT3: vi.CAPACITY_GRAIN_FT3 ?? 0,
        SCRUBBER_TYPE: vi.SCRUBBER_TYPE,
      };

      setVoyageVesselInfo(vesselInfo);
      setIsLoading(false);
    } catch (error: unknown) {
      logE(new Error("VoyagePage - fetchVoyageVesselInfo - failed"));
      setIsLoading(false);
    }
  };

  const formatCoordinates = (latitude: number, longitude: number) => {
    const latDirection = latitude >= 0 ? "N" : "S";
    const lonDirection = longitude >= 0 ? "E" : "W";
    const formattedLatitude = `${latitude.toFixed(4)}º${latDirection}`;
    const formattedLongitude = `${longitude.toFixed(4)}º${lonDirection}`;

    return `${formattedLatitude} ${formattedLongitude}`;
  };

  const fetchVoyageSequenceItinerary = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/voyageSequenceItinerary`, {
        params: {
          tc_code: tcCode,
        },
      });
      log("STATE - fetchVoyageSequenceItinerary - apply", { count: result.data?.length ?? 0 });
      setVoyageInfoData(result.data);
      setIsLoading(false);
    } catch (error: unknown) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tcCode) {
      log("VoyagePage - INIT", { tcCode });
      fetchVoyageMovementsPort();
      fetchVoyageSequenceItinerary();
      fetchVoyageVesselInfo();
    }
  }, [tcCode]);

  const prevImo = useRef<number | null>(null);

  useEffect(() => {
    const newImo: number | undefined | null = vesselInfo?.IMO_NUMBER ?? voyageInfoData?.[0]?.IMO_NUMBER;
    if (newImo && newImo !== prevImo.current) {
      prevImo.current = newImo;
      fetchMapData(newImo);
    }
  }, [vesselInfo, voyageInfoData]);

  logV("VoyagePage - BUILD", { loading: isLoading, count: voyageInfoData.length });

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div className={twMerge("flex h-screen", theme.home.tailwind.backgound)}>
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-auto">
          <header className={twMerge("border-b-[1px] pb-2 pl-6 pt-4", theme.uwhcAssistant.border)}>
            <Header />
          </header>
          <main>
            <div className="flex gap-6 px-6">
              <VesselInformation className="min-w-80 max-w-80" vesselInfo={voyageVesselInfo} />
              <VerticalDivider />
              <ChecklistTab className="flex-grow" mapData={mapData} />
              <VerticalDivider />
              <VoyageInformation className="min-w-80 max-w-80" voyageInfoData={voyageInfoData} />
            </div>
          </main>
          <PageFooter />
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default VoyagePage;
